import React, { useState, useEffect, useCallback } from "react";
import {
  Input,
  Button,
  Radio,
  Form,
  Table,
  Modal,
  UploadProps,
  Switch,
  Upload,
  Typography,
} from "antd";
import {
  DeleteOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  EditOutlined,
  InboxOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import Dragger from "antd/es/upload/Dragger";

import DeleteConfirmation from "../../components/DeleteConfirmation.tsx";
import questionService from "../../services/question-service.ts";
import Constant from "../../global/Constant.ts";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../redux/store.ts";
import { setLoading, addNotification } from "../../redux/globalSlice.ts";

const { TextArea } = Input;

const QuestionForm = () => {
  const loggedUserDetails = useSelector(
    (state: RootState) => state.user.userDetails
  );
  const [visible, setVisible] = useState(false);
  const [visibleBulk, setVisibleBulk] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [questionId, setQuestionId] = useState(0);
  const [form] = Form.useForm();
  const [loading, setLoadingg] = useState(false);
  const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
  const [bulkBtnStatus, setbulkBtnStatus] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalQuestions, setTotalQuestions] = useState(0);
  const [file, setFile] = useState<File | null>(null);
  const [disableQuestion, setDisableQuestion] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();

  const getAllQuestions = useCallback(
    async (page = currentPage, size = perPage) => {
      try {
        dispatch(setLoading(true));
        const response = await questionService.getAllQuestions(
          Constant.question,
          {
            currentPage: page,
            perPage: size,
          }
        );
        setQuestions(response.Questions);
        setTotalQuestions(response.totalQuestions);
        dispatch(setLoading(false));
      } catch (error) {
        dispatch(setLoading(false));
        setQuestions([]);
        console.error("Error fetching questions:", error);
      }
    },
    [dispatch, currentPage, perPage]
  );

  useEffect(() => {
    getAllQuestions();
  }, [currentPage, perPage, getAllQuestions]);

  const validateQuestionForm = (values) => {
    if (!values.options || values.options.length !== 4) {
      dispatch(
        addNotification({
          title: "Warning",
          description: "Please add options to question",
          type: "warning",
        })
      );
      return false;
    }

    if (
      values.correctOption == null ||
      values.correctOption === undefined ||
      values.correctOption === ""
    ) {
      dispatch(
        addNotification({
          title: "Warning",
          description: "Please select a correct option.",
          type: "warning",
        })
      );
      return false;
    }

    return true;
  };

  const buildQuestionPayload = (values) => ({
    QuestionText: values.question,
    Answer1: values.options[0]?.option || "",
    Answer2: values.options[1]?.option || "",
    Answer3: values.options[2]?.option || "",
    Answer4: values.options[3]?.option || "",
    IsCorrect: values.options[values.correctOption]?.option || "",
    Hint: values.hint,
    Status: disableQuestion,
    CreatedBy: loggedUserDetails?.UserID,
  });

  const saveQuestion = async (payload, isEdit) => {
    if (isEdit) {
      payload.QuestionID = questionId;
      await questionService.updateQuestion(Constant.question, payload);
      dispatch(
        addNotification({
          title: "Success",
          description: "Question is updated",
          type: "success",
        })
      );
    } else {
      await questionService.createQuestion(Constant.question, payload);
      dispatch(
        addNotification({
          title: "Success",
          description: "Question is created",
          type: "success",
        })
      );
    }
  };

  const handleAddQuestion = async () => {
    try {
      setLoadingg(true);
      const values = await form.validateFields();

      if (!validateQuestionForm(values)) {
        setLoadingg(false);
        return;
      }

      const payload = buildQuestionPayload(values);
      await saveQuestion(payload, questionId !== 0);

      form.resetFields();
      setVisible(false);
      setQuestionId(0);

      if (questionId === 0) {
        setCurrentPage(1);
        await getAllQuestions(1, perPage);
      } else {
        await getAllQuestions(currentPage, perPage);
      }
    } catch (error) {
      if (error.errorFields && error.errorFields.length > 0) return;
      if (error?.response?.data?.error) {
        dispatch(
          addNotification({
            title: "Failed",
            description: error?.response?.data?.error,
            type: "error",
          })
        );
      } else {
        dispatch(
          addNotification({
            title: "Failed",
            description: "An unexpected error occurred. Please try again.",
            type: "error",
          })
        );
      }
      console.error("Error adding/updating question:", error);
    } finally {
      setLoadingg(false);
    }
  };

  const handleCloseQuestionModal = () => {
    form.resetFields();
    setVisible(false);
  };

  const handleBulkUpload = async (itemFile: File) => {
    try {
      setbulkBtnStatus(true);
      if (itemFile.size > 5 * 1024 * 1024) {
        dispatch(
          addNotification({
            title: "Warning",
            description: "File size must be less than 5 MB",
            type: "warning",
          })
        );
        return;
      }

      const response = await questionService.uploadCSV(
        Constant.question,
        itemFile,
        loggedUserDetails?.UserID
      );

      dispatch(
        addNotification({
          title: "Success",
          description: response?.data?.message,
          type: "success",
        })
      );

      setVisibleBulk(false);
      setCurrentPage(1);
      await getAllQuestions(1, perPage);
    } catch (error) {
      if (error?.response?.data?.error) {
        dispatch(
          addNotification({
            title: "Failed",
            description: error?.response?.data?.error,
            type: "error",
          })
        );
      } else {
        dispatch(
          addNotification({
            title: "Failed",
            description: "An unexpected error occurred. Please try again.",
            type: "error",
          })
        );
      }
      console.log("Error importing questions");
    } finally {
      setFile(null);
      setbulkBtnStatus(false);
    }
  };

  const handleDelete = (index) => {
    setDeleteModalVisible(true);
    setDeleteIndex(index);
  };

  const handleAddQuestionModal = () => {
    dispatch(setLoading(true));
    setFile(null);
    setQuestionId(0);
    setDisableQuestion(1);
    form.setFieldsValue({
      disableQuestion: 1,
    });
    setVisible(true);
    dispatch(setLoading(false));
  };

  const handleDeleteConfirm = async () => {
    try {
      setIsDeleting(true);
      await questionService.deleteQuestion(Constant.question, deleteIndex);
      dispatch(
        addNotification({
          title: "Success",
          description: "Question is deleted",
          type: "success",
        })
      );
      setDeleteModalVisible(false);

      const remainingQuestions = totalQuestions - 1;
      const maxPage = Math.ceil(remainingQuestions / perPage);
      const newPage = currentPage > maxPage ? maxPage : currentPage;

      setCurrentPage(newPage);
      await getAllQuestions(newPage, perPage);
    } catch (error) {
      console.error("Error deleting question:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEdit = (record) => {
    setVisible(true);
    setQuestionId(record?.QuestionID);

    const options = [
      { option: record.Answer1 },
      { option: record.Answer2 },
      { option: record.Answer3 },
      { option: record.Answer4 },
    ];

    const correctIndex = options.findIndex(
      (opt) => opt.option === record.IsCorrect
    );

    form.setFieldsValue({
      question: record.QuestionText,
      hint: record.Hint,
      options: options,
      correctOption: correctIndex !== -1 ? correctIndex : null,
      disableQuestion: record.Status,
    });

    setDisableQuestion(record.Status);
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
  };

  const columns = [
    {
      title: "Sl.No",
      key: "slNo",
      width: 80,
      align: "left",
      className: "vm__no-wrap",
      render: (_, record, index) => {
        return (currentPage - 1) * perPage + index + 1;
      },
    },
    {
      title: "Questions",
      dataIndex: "QuestionText",
      key: "QuestionText",
      className: "vm__min-w-lg",
    },
    {
      title: "Status",
      dataIndex: "Status",
      key: "Status",
      width: 100,
      className: "vm__no-wrap",
      render: (status) =>
        status === 1 ? (
          <CheckOutlined style={{ color: "green" }} />
        ) : (
          <CloseOutlined style={{ color: "red" }} />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      className: "vm__no-wrap",
      render: (_, record) => (
        <div className="vm__d-flex vm__gap-2">
          <span className="vm__table-icons" onClick={() => handleEdit(record)}>
            <EditOutlined />
          </span>
          <span
            className="vm__table-icons"
            onClick={() => handleDelete(record.QuestionID)}
          >
            <DeleteOutlined />
          </span>
        </div>
      ),
    },
  ];

  const uploadProps: UploadProps = {
    name: "file",
    multiple: false,
    accept: ".csv",
    beforeUpload: (itemFile) => {
      if (itemFile.size > 5 * 1024 * 1024) {
        dispatch(
          addNotification({
            title: "Warning",
            description: "File size must be less than 5 MB",
            type: "warning",
          })
        );
        return Upload.LIST_IGNORE;
      }
      setFile(itemFile);
      return false;
    },
    onRemove: () => {
      setFile(null);
    },
  };

  return (
    <div className="vm__container vm__h-100">
      <div className="vm__d-flex vm__flex-column vm__h-100">
        <div className="vm__flex-auto">
          <div className="vm__d-flex vm__mb-3 vm__align-items-center vm__flex-wrap vm__gap-2">
            <div className="vm__flex-fill">
              <h3 className="vm__page-header">Questions</h3>
            </div>
            <div className="vm__flex-auto">
              <div className="vm__text-end vm__d-flex vm__gap-2">
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisibleBulk(true);
                  }}
                >
                  Import Questions
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => handleAddQuestionModal()}
                >
                  Add Question
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="vm__flex-fill vm__overflow-auto">
          <div className="vm__custom-table">
            <Table
              dataSource={questions}
              columns={questions && questions.length > 0 ? columns : []}
              rowKey={(record, index) => index}
              className={
                questions && questions.length > 0 ? "vm__table-container" : ""
              }
              scroll={{ x: true }}
              pagination={{
                current: currentPage,
                pageSize: perPage,
                total: totalQuestions,
                onChange: (page, pageSize) => {
                  setCurrentPage(page);
                  setPerPage(pageSize);
                  getAllQuestions(page, pageSize);
                },
              }}
            />
          </div>
        </div>
      </div>

      <DeleteConfirmation
        isVisible={isDeleteModalVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmLoading={isDeleting}
      />

      <Modal
        title={questionId !== 0 ? "Edit Question" : "Add Question"}
        visible={visible}
        onCancel={handleCloseQuestionModal}
        onOk={handleAddQuestion}
        confirmLoading={loading}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
        width={600}
        className="vm__modal"
        maskClosable={false}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="question"
            label="Question Text"
            rules={[
              { required: true, message: "" },
              { whitespace: true, message: "" },
              {
                max: 500,
                message: "Question Text must be under 500 characters",
              },
            ]}
            className="vm__mb-2"
          >
            <TextArea placeholder="Enter your question here..." rows={3} />
          </Form.Item>
          <Form.Item
            name="hint"
            label="Explanation"
            className="vm__mb-0"
            rules={[
              { required: true, message: "" },
              { whitespace: true, message: "" },
              { max: 1500, message: "Explanation must be under 1500 characters" },
            ]}
            extra={
              <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>
                Add helpful explanation (max 1500 chars). For Knowledge Hub
                links, wrap the URL in curly braces like{" "}
                {"{https://example.com}"}
              </div>
            }
          >
            <Input placeholder="Add a helpful explanation..." />
          </Form.Item>

          <Form.List name="options">
            {(fields, { add, remove }) => (
              <>
                <Radio.Group
                  className="vm__w-100 vm__mt-3"
                  value={form.getFieldValue("correctOption")}
                  onChange={(e) =>
                    form.setFieldsValue({ correctOption: e.target.value })
                  }
                >
                  {fields.map(({ key, name, ...restField }, index) => (
                    <div key={key} className="vm-option__row">
                      <Form.Item {...restField} className="vm-option__radio">
                        <Radio value={index} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, "option"]}
                        rules={[
                          { required: true, message: "" },
                          {
                            whitespace: true,
                            message: "",
                          },
                        ]}
                        className="vm-option__input"
                      >
                        <Input placeholder="Enter option" />
                      </Form.Item>
                      <MinusCircleOutlined
                        onClick={() => remove(name)}
                        className="vm-option__remove"
                        style={{ cursor: "pointer", fontSize: 16 }}
                      />
                    </div>
                  ))}
                </Radio.Group>
                <div className="vm__d-flex vm__gap-3">
                  {fields.length < 4 && (
                    <div className="vm__flex-auto">
                      <Button
                        type="primary"
                        onClick={() => {
                          add();
                        }}
                        icon={<PlusOutlined />}
                        className="vm-add-option"
                      >
                        Add Option
                      </Button>
                    </div>
                  )}
                  <div className="vm__flex-fill">
                    <div className="vm__content-action-btn">
                      <Form.Item name="disableQuestion" className="vm__mb-0">
                        <span className="vm__label-container">Status: </span>
                        <Switch
                          checked={disableQuestion === 1}
                          onChange={(checked) =>
                            setDisableQuestion(checked ? 1 : 0)
                          }
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </>
            )}
          </Form.List>

          <Form.Item name="correctOption" hidden>
            <Input />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Import Questions"
        visible={visibleBulk}
        maskClosable={false}
        onCancel={() => {
          setVisibleBulk(false);
          setFile(null);
          setbulkBtnStatus(false);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setVisibleBulk(false);
              setFile(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="ok"
            type="primary"
            disabled={bulkBtnStatus}
            onClick={async () => {
              if (file) {
                try {
                  await handleBulkUpload(file);
                  setVisibleBulk(false);
                  setFile(null);
                } catch (error) {
                  console.error("Error uploading file:", error);
                }
              } else {
                dispatch(
                  addNotification({
                    title: "Warning",
                    description: "Please select a file to upload.",
                    type: "warning",
                  })
                );
              }
            }}
          >
            OK
          </Button>,
        ]}
        bodyStyle={{ maxHeight: "60vh", overflowY: "auto" }}
        width={500}
        className="vm__modal"
      >
        <div className="vm__text-end">
          <Typography.Text type="secondary" italic>
            <a
              className="vm__mb-2 vm__d-inline-block"
              href="./sample_question.csv"
              download="sample_question.csv"
            >
              Download Sample Question CSV
            </a>
          </Typography.Text>
        </div>
        <div className="vm__text-end">
          <Typography.Text type="secondary" italic>
            Maximum number of questions to be imported is 100
          </Typography.Text>
        </div>
        <Dragger
          maxCount={1}
          {...uploadProps}
          fileList={file ? [file] : []}
          className="vm__dragger"
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">
            Click or drag file to this area to upload
          </p>
          <p className="ant-upload-hint">
            Please upload a CSV file. Maximum file size is 5 MB and only 100
            rows are allowed.
          </p>
        </Dragger>
      </Modal>
    </div>
  );
};

export default QuestionForm;
